// EN-4743. TD3 passport MRZ (Machine Readable Zone) parsing, ICAO Doc 9303 Part 4.
//
// Ported from the kiosk: visitly-kiosk-app/src/utils/mrz.js @ d4e6219. The logic
// is kept identical so both apps read a passport the same way - change them
// together. Only TypeScript types were added.
//
// The MRZ is the two 44-character lines printed at the bottom of a passport's data
// page. `parseMrzPair` parses an already-identified pair; `findAndParseMrz` finds
// the pair in the noisy, unordered list of lines text recognition produces.

export interface MrzLine {
  text: string;
  confidence?: number;
  // Vertical position of the line in the recognized image. Only the distance
  // between lines is used, so any consistent unit works. Other box fields are
  // allowed and ignored.
  boundingBox?: { y: number; [key: string]: number };
}

export interface MrzFieldChecks {
  documentNumber: boolean;
  dateOfBirth: boolean;
  dateOfExpiry: boolean;
  personalNumber: boolean;
  composite: boolean;
}

export interface MrzParseResult {
  documentType: string;
  issuingCountry: string;
  surname: string;
  givenNames: string;
  documentNumber: string;
  nationality: string;
  // YYYY-MM-DD, or null when the MRZ field wasn't six digits.
  dateOfBirth: string | null;
  sex: 'M' | 'F' | null;
  dateOfExpiry: string | null;
  personalNumber: string;
  fieldChecks: MrzFieldChecks;
}

// ICAO 9303's check-digit weights, applied cyclically over every character in the
// field being checked.
const CHECK_DIGIT_WEIGHTS = [7, 3, 1];

// A TD3 line's only valid characters are A-Z, 0-9, and the filler chevron '<'.
// Anything else on a candidate line means a misread character, or that the line
// isn't the MRZ at all.
const MRZ_LINE_PATTERN = /^[A-Z0-9<]+$/;
const TD3_LINE_LENGTH = 44;

// Line 2 (document number/dates) is validated by its own check digits, so
// length+charset is the only pre-filter it needs. Kept tight: a dropped trailing
// character here would be treated as filler and could give a wrong document
// number or date.
const DATA_LINE_LENGTH_TOLERANCE = 2;
const isPlausibleDataLine = (line: unknown): line is string =>
  typeof line === 'string' &&
  Math.abs(line.length - TD3_LINE_LENGTH) <= DATA_LINE_LENGTH_TOLERANCE &&
  MRZ_LINE_PATTERN.test(line);

// Line 1 (document type/name) carries no check digit of its own, so it can afford,
// and on real hardware needs, a far looser length check than line 2: text
// recognition often under-runs the trailing filler while reading the name
// perfectly. The structural checks (starts with 'P', contains the '<<'
// surname/given-names separator) do the discriminating work length can't.
const MIN_NAME_LINE_LENGTH = 10; // "P<" + 3-letter country + a few name characters
const isPlausibleNameLine = (line: unknown): line is string =>
  typeof line === 'string' &&
  line.length >= MIN_NAME_LINE_LENGTH &&
  line.length <= TD3_LINE_LENGTH + DATA_LINE_LENGTH_TOLERANCE &&
  MRZ_LINE_PATTERN.test(line) &&
  line[0] === 'P' &&
  line.includes('<<');

// ICAO 9303's character values: digits are themselves, A-Z are 10-35, '<' is 0.
// Anything else contributes 0, so the check digit comes out wrong rather than the
// function throwing.
const charValue = (ch: string): number => {
  if (ch === '<') return 0;
  if (ch >= '0' && ch <= '9') return ch.charCodeAt(0) - 48;
  if (ch >= 'A' && ch <= 'Z') return ch.charCodeAt(0) - 55;
  return 0;
};

/** ICAO 9303 check digit: weighted sum (7,3,1 cycling) of character values, mod 10. */
export const computeCheckDigit = (field: string): number => {
  let sum = 0;
  for (let i = 0; i < field.length; i += 1) {
    sum += charValue(field[i]) * CHECK_DIGIT_WEIGHTS[i % 3];
  }
  return sum % 10;
};

// A check digit may be '<' when the field it covers is entirely unused (chiefly the
// optional personal number) - ICAO 9303 permits this.
const checkDigitMatches = (field: string, checkDigitChar: string): boolean =>
  checkDigitChar === '<' ? /^<+$/.test(field) : computeCheckDigit(field) === Number(checkDigitChar);

// The name field allows only A-Z and '<', so a digit there is a misread filler
// chevron, never a name character.
const CONTAINS_DIGIT = /\d/;

// The letters a filler chevron gets misread as. OCR-B's '<' is a thin wedge and
// degrades into angular letters, never round or tall ones, which is what makes a
// token made only of these separable from a real name.
const CHEVRON_LOOKALIKES = /^[KCESX6<]+$/;

const isMisreadFiller = (token: string): boolean =>
  CONTAINS_DIGIT.test(token) || CHEVRON_LOOKALIKES.test(token);

/**
 * Drop the misread filler run off the end of the given names.
 *
 * Line 1 has no check digit, so a valid composite says nothing about the name. A
 * real capture came back as `HAPPY KEKKE ECCKCKKKS 6KCKK` with every check digit
 * passing: the name is `HAPPY` and the rest is misread filler.
 *
 * Only the tail is trimmed, and never the first token, so a given name that really
 * is all angular letters (ESSEX, CECE) is kept.
 */
const stripFillerNoise = (tokens: string[]): string[] => {
  const kept = [...tokens];
  while (kept.length > 1 && isMisreadFiller(kept[kept.length - 1])) {
    kept.pop();
  }
  return kept;
};

// Line 1: P<CCCSURNAME<<GIVEN<NAMES<<<<<<<<<<<<<<<<<<<<
// Positions 6-44 are one filler-padded block: before the first '<<' is the
// surname, after it the given names; a single '<' inside either stands for a space.
const parseNameField = (line1: string): { surname: string; givenNames: string } => {
  const nameField = line1.slice(5, 44);
  const [surnamePart, ...rest] = nameField.split('<<');

  // Left as read: the surname isn't next to the filler run, and surnames are the
  // likelier of the two to be genuinely unusual.
  const surname = surnamePart.replace(/</g, ' ').trim();

  // A second '<<' in the given names isn't valid TD3; treat it as a single
  // separator rather than throw a name away.
  const givenNameTokens = rest
    .join('<')
    .split('<')
    .map((token) => token.trim())
    .filter(Boolean);

  return { surname, givenNames: stripFillerNoise(givenNameTokens).join(' ') };
};

// How far ahead an expiry date can plausibly sit. A passport is valid for at most 10
// years; 20 leaves room for longer-lived documents while still placing "98" in 1998.
const MAX_EXPIRY_YEARS_AHEAD = 20;

// Two-digit MRZ years need a century.
//
// Birth date: nobody was born in the future, so a birth year greater than the
// current two-digit year is 1900s.
//
// Expiry date: the century that puts it inside a 100-year window ending
// MAX_EXPIRY_YEARS_AHEAD years from now. An expired passport's "98" is 1998, not
// 2098. Relative to the current year, so it keeps working past 2100.
export const resolveYear = (twoDigitYear: string, kind: 'birth' | 'expiry', now: Date = new Date()): number => {
  const yy = Number(twoDigitYear);
  const currentYear = now.getFullYear();

  if (kind !== 'birth') {
    const latest = currentYear + MAX_EXPIRY_YEARS_AHEAD;
    const candidate = currentYear - (currentYear % 100) + yy;
    if (candidate > latest) return candidate - 100;
    if (candidate <= latest - 100) return candidate + 100;
    return candidate;
  }

  const currentYY = currentYear % 100;
  return (yy > currentYY ? 1900 : 2000) + yy;
};

// YYMMDD -> 'YYYY-MM-DD'. Null rather than a guess when the field isn't six
// digits, since that means the line wasn't read cleanly.
const formatMrzDate = (yymmdd: string, kind: 'birth' | 'expiry'): string | null => {
  if (!/^\d{6}$/.test(yymmdd)) return null;
  const year = resolveYear(yymmdd.slice(0, 2), kind);
  const month = yymmdd.slice(2, 4);
  const day = yymmdd.slice(4, 6);
  return `${year}-${month}-${day}`;
};

/**
 * Parse and validate an already-identified TD3 MRZ pair, in either order.
 *
 * Returns null when either line isn't TD3-shaped, or when the composite check
 * digit - ICAO's "is this MRZ trustworthy" signal, covering every other field -
 * fails. `fieldChecks` is reported for diagnostics; only the composite gates.
 *
 * Caveat (kept from the kiosk): some countries print non-compliant
 * personal-number check digits. This follows ICAO literally and includes it in
 * the composite; if real passports fail on it, exclude personalNumber from the
 * gate while still reporting it.
 */
export const parseMrzPair = (rawA: string, rawB: string): MrzParseResult | null => {
  // Told apart by shape, not by argument position.
  const line1 = [rawA, rawB].find(isPlausibleNameLine);
  const line2 = [rawA, rawB].find((line) => line !== line1 && isPlausibleDataLine(line));
  if (!line1 || !line2) return null;

  // Pad/trim to exactly 44 so the fixed-width slices line up. Padding with '<'
  // recovers a trailing filler run text recognition didn't read as far as.
  const l1 = line1.padEnd(TD3_LINE_LENGTH, '<').slice(0, TD3_LINE_LENGTH);
  const l2 = line2.padEnd(TD3_LINE_LENGTH, '<').slice(0, TD3_LINE_LENGTH);

  const documentType = l1.slice(0, 2).replace(/</g, '').trim() || 'P';
  const issuingCountry = l1.slice(2, 5);
  const { surname, givenNames } = parseNameField(l1);

  const documentNumberField = l2.slice(0, 9);
  const documentNumberCheck = l2[9];
  const nationality = l2.slice(10, 13);
  const dobField = l2.slice(13, 19);
  const dobCheck = l2[19];
  const sexChar = l2[20];
  const expiryField = l2.slice(21, 27);
  const expiryCheck = l2[27];
  const personalNumberField = l2.slice(28, 42);
  const personalNumberCheck = l2[42];
  const compositeCheck = l2[43];

  const fieldChecks: MrzFieldChecks = {
    documentNumber: checkDigitMatches(documentNumberField, documentNumberCheck),
    dateOfBirth: checkDigitMatches(dobField, dobCheck),
    dateOfExpiry: checkDigitMatches(expiryField, expiryCheck),
    personalNumber: checkDigitMatches(personalNumberField, personalNumberCheck),
    // The composite covers the four fields above plus their check digits, not the
    // whole line, per ICAO 9303.
    composite: checkDigitMatches(
      documentNumberField + documentNumberCheck + dobField + dobCheck + expiryField + expiryCheck + personalNumberField + personalNumberCheck,
      compositeCheck,
    ),
  };

  if (!fieldChecks.composite) return null;

  return {
    documentType,
    issuingCountry,
    surname,
    givenNames,
    documentNumber: documentNumberField.replace(/</g, ''),
    nationality,
    dateOfBirth: formatMrzDate(dobField, 'birth'),
    sex: sexChar === 'M' || sexChar === 'F' ? sexChar : null,
    dateOfExpiry: formatMrzDate(expiryField, 'expiry'),
    personalNumber: personalNumberField.replace(/</g, ''),
    fieldChecks,
  };
};

/**
 * Find the MRZ pair among recognized text lines and parse it.
 *
 * Line 1 is found first by its own shape, then line 2 is the closest data-shaped
 * line to it: the two MRZ lines are printed closer to each other than to anything
 * else on the page. The two checks run in that order because a padded line 1 would
 * also pass the plain data-line check.
 */
// Takes `unknown` like the kiosk version: anything that isn't an array is null.
export const findAndParseMrz = (recognizedLines: unknown): MrzParseResult | null => {
  if (!Array.isArray(recognizedLines)) return null;

  const normalized = (recognizedLines as MrzLine[]).map((line) => ({
    ...line,
    text: (line?.text ?? '').toUpperCase().replace(/\s+/g, ''),
  }));

  const line1Candidate = normalized.find((line) => isPlausibleNameLine(line.text));
  if (!line1Candidate) return null;

  const line2Candidates = normalized.filter(
    (line) => line !== line1Candidate && isPlausibleDataLine(line.text),
  );
  if (line2Candidates.length === 0) return null;

  const line1Y = line1Candidate.boundingBox?.y ?? 0;
  const line2Candidate = line2Candidates.reduce<(typeof normalized)[number] | null>((closest, line) => {
    const distance = Math.abs((line.boundingBox?.y ?? 0) - line1Y);
    const closestDistance = closest ? Math.abs((closest.boundingBox?.y ?? 0) - line1Y) : Infinity;
    return distance < closestDistance ? line : closest;
  }, null);

  return line2Candidate ? parseMrzPair(line1Candidate.text, line2Candidate.text) : null;
};
